import type { TaskStatus } from '@developerz/domain';
import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { StatusPill, type StatusTone } from './StatusPill';

// One review run's status, in the fleet task vocabulary — a review IS a
// `pr_review` task (spec §3.2: done → success, running → live pulse, failed →
// destructive tint, everything queued/waiting → secondary).
//
// ONE FILE FOR TWO SURFACES. These maps stood inside `<PrReviewRuns/>`; the repo
// Reviews tab (`<RepoReviewsTab/>`) renders the same runs from the repo-wide read,
// and a second copy of a `Record<TaskStatus, …>` is two places for one enum to
// grow out of. The `Record` is exhaustive by type, so a new `TaskStatus` member is
// a compile error here rather than an `undefined` tone on screen.

const TONE: Record<TaskStatus, StatusTone> = {
  pending: 'neutral',
  assigned: 'neutral',
  in_progress: 'live',
  completed: 'success',
  awaiting_ci: 'neutral',
  awaiting_review: 'neutral',
  mergeable: 'success',
  merged: 'success',
  failed: 'danger',
  cancelled: 'neutral',
  // #6747: accent is for live work, and a paused task is not running.
  paused: 'neutral',
};

const LABEL: Record<TaskStatus, string> = {
  pending: 'fleet.task_status_pending',
  assigned: 'fleet.task_status_assigned',
  in_progress: 'fleet.task_status_in_progress',
  // A completed REVIEW posted comments — it did not open a PR (unlike the fleet
  // task label "PR opened"), so a review-specific label is used here.
  completed: 'repo_detail.review_runs_completed',
  awaiting_ci: 'fleet.task_status_awaiting_ci',
  awaiting_review: 'fleet.task_status_awaiting_review',
  mergeable: 'fleet.task_status_mergeable',
  merged: 'fleet.task_status_merged',
  failed: 'fleet.task_status_failed',
  cancelled: 'fleet.task_status_cancelled',
  paused: 'fleet.task_status_paused',
};

export function ReviewStatusPill(props: { readonly status: TaskStatus }): JSX.Element {
  return <StatusPill tone={TONE[props.status]}>{t(LABEL[props.status])}</StatusPill>;
}
